import { useState, useEffect } from "react";
import { P, FF } from "../../styles/tokens";
import { api } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { track } from "../../lib/posthog";

/**
 * Modal de bienvenida post-signup que muestra opciones según el plan elegido.
 * Props:
 *   plan: "basico" | "pro" | "gratis"
 *   onClose: () => void
 */
export default function PlanWelcomeModal({ plan, onClose }) {
  const { show: showToast } = useToast();
  const [working, setWorking] = useState(false);

  // Trackear al montar
  useEffect(() => {
    try { track("welcome_modal_shown", { plan }); } catch {}
  }, [plan]);

  const isPaid = plan === "basico" || plan === "pro";
  const planLabel = plan === "pro" ? "Pro" : "Básico";

  async function handleActivateTrial() {
    setWorking(true);
    try {
      await api.subscriptions.startTrial(plan);
      try { track("welcome_modal_action", { action: "trial_activated", plan }); } catch {}
      showToast("¡Trial activado!");
      onClose();
    } catch (e) {
      showToast("Error al activar trial: " + (e?.message || "intenta de nuevo"));
    } finally {
      setWorking(false);
    }
  }

  async function handleCheckout() {
    setWorking(true);
    try {
      const { checkout_url } = await api.subscriptions.checkout(plan);
      try { track("welcome_modal_action", { action: "checkout_started", plan }); } catch {}
      window.location.href = checkout_url;
    } catch (e) {
      showToast("Error al iniciar pago: " + (e?.message || "intenta de nuevo"));
      setWorking(false);
    }
  }

  async function handleTrialPro() {
    setWorking(true);
    try {
      await api.subscriptions.startTrial("pro");
      try { track("welcome_modal_action", { action: "trial_activated", plan: "pro" }); } catch {}
      showToast("¡Trial Pro activado!");
      onClose();
    } catch (e) {
      showToast("Error al activar trial: " + (e?.message || "intenta de nuevo"));
    } finally {
      setWorking(false);
    }
  }

  function handleContinueFree() {
    try { track("welcome_modal_action", { action: "continued_free", plan }); } catch {}
    onClose();
  }

  return (
    /* Backdrop */
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      {/* Modal panel */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: P.surface,
          borderRadius: 24,
          padding: 32,
          maxWidth: 420,
          width: "100%",
          boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          gap: 20,
          fontFamily: FF,
        }}
      >
        {isPaid ? (
          <>
            {/* Paid plan: Basico or Pro */}
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: P.text, lineHeight: 1.3 }}>
                Empezá tu trial de 15 días — sin tarjeta
              </h2>
              <p style={{ margin: "10px 0 0", fontSize: 14, color: P.textSm, lineHeight: 1.6 }}>
                Acceso completo al plan {planLabel} por 15 días. Si no querés continuar, volvés automáticamente a Gratis. Sin sorpresas.
              </p>
            </div>

            <button
              onClick={handleActivateTrial}
              disabled={working}
              style={{
                padding: "14px 20px",
                borderRadius: 14,
                border: "none",
                background: P.brand.primary,
                color: "#fff",
                fontSize: 15,
                fontWeight: 700,
                cursor: working ? "default" : "pointer",
                opacity: working ? 0.7 : 1,
                fontFamily: FF,
                transition: "opacity .15s",
              }}
            >
              {working ? "Activando..." : "Activar trial gratis"}
            </button>

            <button
              onClick={handleCheckout}
              disabled={working}
              style={{
                padding: "14px 20px",
                borderRadius: 14,
                border: `1.5px solid ${P.brand.primary}`,
                background: "transparent",
                color: P.brand.primary,
                fontSize: 15,
                fontWeight: 700,
                cursor: working ? "default" : "pointer",
                opacity: working ? 0.7 : 1,
                fontFamily: FF,
                transition: "opacity .15s",
              }}
            >
              Pagar ahora
            </button>

            <button
              onClick={handleContinueFree}
              disabled={working}
              style={{
                background: "none",
                border: "none",
                color: P.textSm,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: FF,
                textDecoration: "underline",
                textUnderlineOffset: 3,
                padding: 0,
                alignSelf: "center",
              }}
            >
              Empezar gratis primero
            </button>
          </>
        ) : (
          <>
            {/* Free plan */}
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: P.text, lineHeight: 1.3 }}>
                ¡Bienvenido a Arbo!
              </h2>
              <p style={{ margin: "10px 0 0", fontSize: 14, color: P.textSm, lineHeight: 1.6 }}>
                Tu cuenta gratis está lista. ¿Querés probar Pro durante 15 días sin compromiso?
              </p>
            </div>

            <button
              onClick={handleContinueFree}
              style={{
                padding: "14px 20px",
                borderRadius: 14,
                border: "none",
                background: P.brand.primary,
                color: "#fff",
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: FF,
              }}
            >
              Continuar gratis
            </button>

            <button
              onClick={handleTrialPro}
              disabled={working}
              style={{
                padding: "14px 20px",
                borderRadius: 14,
                border: `1.5px solid ${P.brand.primary}`,
                background: "transparent",
                color: P.brand.primary,
                fontSize: 15,
                fontWeight: 700,
                cursor: working ? "default" : "pointer",
                opacity: working ? 0.7 : 1,
                fontFamily: FF,
                transition: "opacity .15s",
              }}
            >
              {working ? "Activando..." : "Probar Pro 15 días"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
